import { useLocation, useNavigate } from 'react-router-dom';
import {
    Grid,
    Typography,
    Box,
    CircularProgress,
    Button,
    Modal,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import MainCard from '../../../components/ui-component/cards/MainCard';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import statusStyleMap from '../../../../utils/statusStyles';
import { resultStyleMap } from '../../../../utils/statusStyles';
import { getClinicalDetail } from '../../../../api/get/clinical';
import { updateClinicalAnnotation } from '../../../../api/patch/clinical';
import { DefaultTextModal } from '../../../components/ui-component/Modal';
import AnimateButton from '../../../components/ui-component/extended/AnimateButton';
import { RESPONSE_CODE, responseResult } from '../../../../metadata/constant';

const ClinicalDetail = () => {
    const { id } = useParams();
    const { state } = useLocation(); // location.state.exam 에서 데이터 꺼냄
    const navigate = useNavigate();

    const [gtList, setGtList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);

    const [openModal, setOpenModal] = useState(0);

    const AnnotationIdMap = {
        COM019_1: '정상',
        COM019_2: '비정상'
    };
    const fetchClinicalDetail = async () => {
        setLoading(true);
        try {
            const response = await getClinicalDetail(id);
            if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                setGtList(response.data);
                setSelectedAnnotation(response.data.annotation_id);
            } else {
                setError('검사 상세 정보를 불러오는 데 실패했습니다.');
            }
        } catch (err) {
            setError('네트워크 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClinicalDetail();
    }, [id]);

    const formatDate = (datestr) => {
        if (!datestr) return '-';
        const date = dayjs(datestr);
        return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : '-';
    };
    const handleAnnotationChange = async () => {
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setSelectedAnnotation(gtList?.annotation_id); // 원래 값으로 복원
    };
    const handleAnnotationSave = async () => {
        try {
            const payload = {
                patient_uid: Number(id),
                annotation_id: selectedAnnotation
            };
            const response = await updateClinicalAnnotation(payload);

            if (responseResult(response.code) === RESPONSE_CODE.SUCCESS) {
                setOpenModal(1); // 저장 성공 시 모달 열기
            } else {
                setOpenModal(2);
            }
        } catch (error) {
            setOpenModal(2);
        }
    };
    const handleModalClose = () => {
        setOpenModal(0);
        setIsEditMode(false);
        fetchClinicalDetail();
    };

    const handleBacktoList = () => {
        navigate('/service/clinical', { state: { from: 'clinicalDetail' } });
    };

    const UncheckedIcon = styled('span')(() => ({
        display: 'inline-block',
        width: 24,
        height: 24,
        borderRadius: 6,
        backgroundColor: '#E3F2FD'
    }));

    const CheckedIcon = styled('span')(() => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        borderRadius: 6,
        backgroundColor: '#E3F2FD',
        color: '#2196F3',
        '& svg': {
            fontSize: 20
        }
    }));

    const CustomRadio = (props) => (
        <Radio
            disableRipple
            icon={<UncheckedIcon />}
            checkedIcon={
                <CheckedIcon>
                    <CheckIcon />
                </CheckedIcon>
            }
            {...props}
        />
    );

    if (loading) {
        return (
            <Grid container justifyContent="center" sx={{ mt: 5 }}>
                <CircularProgress />
            </Grid>
        );
    }

    if (error || !gtList) {
        return (
            <Grid container justifyContent="center" sx={{ mt: 5 }}>
                <Typography color="error">검사 정보를 불러올 수 없습니다.</Typography>
            </Grid>
        );
    }
    const { name, birthday, gender_id, education_id, created_at, annotation_id: rawAnnotationId } = gtList;
    const annotationLabel = AnnotationIdMap[rawAnnotationId] || '-';

    const infoList = [
        { label: '대상자 성명', value: name },
        { label: '생년월일', value: birthday },
        { label: '성별', value: gender_id },
        { label: '학력', value: education_id },
        { label: '검사 등록 시각', value: formatDate(created_at) }
    ];

    return (
        <>
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <MainCard>
                        <Typography variant="h5" sx={{ mb: 5, fontSize: 20 }}>
                            {annotationLabel === '-' ? '입력 대기' : '입력 완료'}
                        </Typography>
                        <Grid container spacing={2} sx={{ borderTop: '1px solid #E3E8EF' }}>
                            {infoList.map((info, idx) => (
                                <Grid item xs={1.71} key={idx} sx={{ mt: 2 }}>
                                    <Typography>{info.label}</Typography>
                                    <Typography color="#777777" sx={{ mt: 2 }}>
                                        {info.value || '-'}
                                    </Typography>
                                    {info.label === '대상자 성명' && (
                                        <>
                                            <Typography sx={{ mt: 6 }}>결과 어노테이션</Typography>
                                            {!isEditMode ? (
                                                <Typography color="#777777" sx={{ mt: 2 }}>
                                                    {annotationLabel}
                                                </Typography>
                                            ) : (
                                                <FormControl sx={{ mt: 1 }}>
                                                    <RadioGroup
                                                        value={selectedAnnotation}
                                                        onChange={(e) => setSelectedAnnotation(e.target.value)}
                                                    >
                                                        <FormControlLabel value="COM019_1" control={<CustomRadio />} label="정상" />
                                                        <FormControlLabel value="COM019_2" control={<CustomRadio />} label="비정상" />
                                                    </RadioGroup>
                                                </FormControl>
                                            )}
                                        </>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                        <Grid container justifyContent="flex-end" sx={{ mt: 5, borderTop: '1px solid #E3E8EF' }} spacing={2}>
                            {isEditMode ? (
                                <>
                                    <Grid item>
                                        <AnimateButton>
                                            <Button variant="text" size="large" onClick={handleCancelEdit} sx={{ mt: 3, color: '#697586' }}>
                                                취소하고 돌아가기
                                            </Button>
                                        </AnimateButton>
                                    </Grid>
                                    <Grid item>
                                        <AnimateButton>
                                            <Button
                                                variant="contained"
                                                disableElevation
                                                color="primary"
                                                size="large"
                                                onClick={handleAnnotationSave}
                                                sx={{ mt: 3, backgroundColor: '#F9A825' }}
                                            >
                                                어노테이션 저장
                                            </Button>
                                        </AnimateButton>
                                    </Grid>
                                </>
                            ) : (
                                <Grid item>
                                    <AnimateButton>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            size="large"
                                            color="secondary"
                                            onClick={handleAnnotationChange}
                                            sx={{ mt: 3 }}
                                        >
                                            어노테이션 정보 수정
                                        </Button>
                                    </AnimateButton>
                                </Grid>
                            )}
                        </Grid>
                    </MainCard>
                </Grid>
            </Grid>
            <DefaultTextModal
                title="어노테이션 저장 완료"
                content="저장이 완료되었습니다."
                open={openModal === 1}
                onClose={handleModalClose}
            />
            <DefaultTextModal
                open={openModal === 2}
                title="오류가 발생하였습니다."
                content={`요청에 실패하였습니다\n새로고침 버튼을 선택하여 다시 시도해보세요.\n오류가 지속되는 경우 고객센터로 문의 부탁드립니다.`}
                onClose={handleModalClose}
            />
        </>
    );
};

export default ClinicalDetail;
